import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import clx from 'classnames';
import { Lock, UserMinus } from 'lucide-react';

import styles from './RemoveFromQueueButton.module.scss';
import ConfirmModal from 'Containers/Common/ConfirmModal';
import { modalActions } from 'Containers/Modals/store';
import { schedulerActions } from 'Containers/Scheduler/store';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import useWindowSize from 'utils/useWindowSize';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';

const formatQueueDate = (date) => {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

/**
 * Azione "Rimuovi dalla coda" (solo piani Premium/Pro; nascosta agli account staff).
 * variant: 'icon' (bottone quadrato) | 'menu' (voce di menu full-width) | 'text' (pill con testo)
 * queueId null → azione disabilitata ("Disponibile a breve": il BE non espone ancora l'id).
 */
const RemoveFromQueueButton = ({
  queueId,
  clientName,
  workerName,
  date,
  variant = 'icon',
  size = '36px',
  label: labelOverride,
  /** Variante text senza icona, per righe strette (mobile). */
  compact = false,
  onRemoved,
  onOpenChange,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const isPremiumUnlocked = useIsPremiumUnlocked();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isStaff = Boolean(localStorage.getItem('staff'));
  if (isStaff) return null;

  const isComingSoon = queueId == null;
  const isLocked = !isPremiumUnlocked;
  const formattedDate = formatQueueDate(date);

  const setOpen = (open) => {
    setIsConfirmOpen(open);
    onOpenChange?.(open);
  };

  const goToUpgrade = () => {
    navigate(PREMIUM_UPGRADE_PATH);
  };

  const handleClick = (event) => {
    event?.stopPropagation();
    event?.preventDefault();
    if (isComingSoon) return;
    if (isLocked) {
      goToUpgrade();
      return;
    }
    setOpen(true);
  };

  const handleConfirm = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    dispatch(
      schedulerActions.REMOVE_FROM_QUEUE.REQUEST(
        { idListaAttesa: queueId, date: date ? dayjs(date).format('YYYY-MM-DD') : undefined },
        (errorCode) => {
          setIsSubmitting(false);
          setOpen(false);
          if (!errorCode) onRemoved?.();
        },
      ),
    );
  };

  const label = labelOverride || t('scheduler.queue.remove.action');
  const title = isComingSoon
    ? t('scheduler.queue.remove.comingSoon')
    : isLocked
      ? t('scheduler.queue.remove.premiumOnly')
      : label;

  const icon = (
    <span className={styles.icon_wrap} aria-hidden="true">
      <UserMinus size={variant === 'icon' ? 17 : 15} strokeWidth={2.3} />
      {isLocked && !isComingSoon && variant !== 'text' ? (
        <Lock size={10} strokeWidth={2.8} className={styles.lock_badge} />
      ) : null}
    </span>
  );

  return (
    <>
      <button
        type="button"
        className={clx(
          styles.trigger,
          styles[`trigger_${variant}`],
          {
            [styles.trigger_locked]: isLocked && !isComingSoon,
            [styles.trigger_disabled]: isComingSoon,
            [styles.trigger_compact]: compact && variant === 'text',
          },
          className,
        )}
        style={variant === 'icon' ? { width: size, height: size } : undefined}
        onClick={handleClick}
        disabled={isComingSoon}
        aria-label={variant === 'icon' ? title : undefined}
        title={title}
      >
        {compact && variant === 'text' ? null : icon}
        {variant === 'text' ? (
          <>
            <span className={styles.trigger_label}>{label}</span>
            {isLocked && !isComingSoon ? (
              <span className={styles.premium_chip}>
                <Lock size={10} strokeWidth={2.8} aria-hidden="true" />
                {t('scheduler.queue.remove.premiumShort')}
              </span>
            ) : null}
          </>
        ) : null}
        {variant === 'menu' ? (
          <span className={styles.trigger_copy}>
            <span className={styles.trigger_label}>{label}</span>
            {isComingSoon ? (
              <span className={styles.trigger_hint}>{t('scheduler.queue.remove.comingSoon')}</span>
            ) : isLocked ? (
              <span className={styles.trigger_hint}>{t('scheduler.queue.remove.premiumOnly')}</span>
            ) : null}
          </span>
        ) : null}
      </button>

      <ConfirmModal
        isModalOpen={isConfirmOpen}
        setIsModalOpen={(open) => {
          if (isSubmitting) return;
          setOpen(Boolean(open));
        }}
        Icon={UserMinus}
        variant="danger"
        title={t('scheduler.queue.remove.confirmTitle', { name: clientName })}
        subtitle={t('scheduler.queue.remove.confirmSubtitle', {
          worker: workerName,
          date: formattedDate,
        })}
        cancelLabel={t('common.actions.cancel')}
        confirmLabel={
          isSubmitting ? t('scheduler.queue.remove.removing') : t('scheduler.queue.remove.confirm')
        }
        action={handleConfirm}
      />
    </>
  );
};

export default RemoveFromQueueButton;
